import {FC, useEffect, useState} from 'react';
import {toast} from 'react-toastify';
//mui
import {Box} from '@mui/material';
//store
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
//formik
import {Formik} from 'formik';
import {AuctionFormValues, initialValues, validationSchema} from './formConfiguration';
//components
import FormContent from './FormContent';
//services
import {OrganizationAuctionService} from '@/utils/services';
//types
import {IOrganizationAuctionDonors, IOrganizationAuctionResponse} from '@/utils/types';
//helpers
import {getErrorMessage} from '@/utils/helpers';
import {getEditableAuctionInitialValues} from './helpers';
import axios from 'axios';
import {getAuction} from '@/store/organization-auction';
import {AUCTION_ACTIVITY} from '@/utils/enums';
import {orgAPI} from '@/core/api';

interface AuctionItemProps {
	onCloseModal: () => void;
	editableAuction: IOrganizationAuctionResponse | null;
	onSuccess?: () => void
}

export const sendPresignedImages = async (presignedImgUrl: any, images: any) => {
	await Promise.all(
		presignedImgUrl.data.map(async (data: {url: string; key: string}, index: number) => {
			const file = images[index];
			if (!file) return;
			await axios.put(data.url, file, {
				headers: {
					'Content-Type': file.type,
				},
			});
		})
	);
};
export const sendPresignedDonorImages = async (presignedImgUrl: any, file: any) => {
	await axios.put(presignedImgUrl, file, {
		headers: {
			'Content-Type': file.type,
		},
	});
};

export const getUpdateImagesData = async (values: AuctionFormValues) => {
	const imagesData = await Promise.all(
		values.images
			.filter((image) => image !== null)
			.map(async (image, index) => {
				if (image instanceof File) {
					const presignedImgUrl = await OrganizationAuctionService.getPresingUrl();
					await axios.put(presignedImgUrl.data[0].url, image, {
						headers: {
							'Content-Type': image.type,
						},
					});
					return {
						image: presignedImgUrl.data[0].key.split('/').pop()!,
						isMain: index === 0,
					};
				}
				return {
					image: image.split('/')[4].split('?')[0],
					isMain: index === 0,
				};
			})
	);
	return imagesData;
};

export const getUpdateDonorImagesData = async (values: AuctionFormValues, editableAuction: any) => {
	const donorsData = await Promise.all(
		values.donors.map(async (donor) => {
			if (donor.logoUrl instanceof File) {
				const presignedImgUrl = await OrganizationAuctionService.getPresingUrlForDonor();
				await axios.put(presignedImgUrl.data.url, donor.logoUrl, {
					headers: {
						'Content-Type': donor.logoUrl.type,
					},
				});
				try {
					await OrganizationAuctionService.createDonor(editableAuction.id, {
						logo: presignedImgUrl.data.key.split('/').pop()!,
						name: donor.name,
					});
					return {
						logo: presignedImgUrl.data.key.split('/').pop()!,
						name: donor.name,
					};
				} catch (error) {
					toast.error(getErrorMessage(error));
				}
			} else return {logo: donor.logoUrl.split('/')[4].split('?')[0], name: donor.name};
		})
	);
	return donorsData;
};

const AuctionItem: FC<AuctionItemProps> = ({onCloseModal, editableAuction, onSuccess}) => {
	const [auctionFormInitialValues, setAuctionFormInitialValues] = useState<AuctionFormValues>(initialValues);
	const dispatch = useAppDispatch();

	useEffect(() => {
		if (!editableAuction) return;
		const getAuctionData = async () => {
			const auction = await dispatch(getAuction({id: editableAuction?.id, apiInstance: orgAPI})).unwrap();
			setAuctionFormInitialValues(getEditableAuctionInitialValues(auction));
		};
		getAuctionData();
	}, []);

	const currentEvent = useAppSelector(getCurrentEventSelector);
	const handleSubmitClickHandler = async (values: AuctionFormValues) => {
		try {
			if (editableAuction) {
				const finalImages = await getUpdateImagesData(values);

				const finalDonors = await getUpdateDonorImagesData(values, editableAuction);

				const reqBody = {
					...values,
					images: finalImages,
					donors: finalDonors as IOrganizationAuctionDonors[],
					status: editableAuction.status === 1 ? AUCTION_ACTIVITY.ACTIVE : AUCTION_ACTIVITY.CLOSED,
				};
				await OrganizationAuctionService.updateAuction(editableAuction.id, reqBody);
				onCloseModal();
			} else {
				const presignedImgUrl = await OrganizationAuctionService.getPresingUrl();
				await sendPresignedImages(presignedImgUrl, values.images);
				const images = presignedImgUrl.data.slice(0, values.images.length).map((data, index) => {
					return {image: data.key.split('/').pop()!, isMain: index === 0};
				});
				const reqBody = {...values, eventId: currentEvent!.id, images, donors: []};
				const {data} = await OrganizationAuctionService.createAuction(reqBody);
				if (data) {
					await Promise.all(
						values.donors.map(async (donor) => {
							const presignedDonorsUrl = await OrganizationAuctionService.getPresingUrlForDonor();
							await sendPresignedDonorImages(presignedDonorsUrl.data.url, donor.logoUrl);
							const donorImageData = {
								name: donor.name,
								logo: presignedDonorsUrl.data.key.split('/').pop()!,
							};
							await OrganizationAuctionService.createDonor(data, donorImageData);
						})
					);
				}
				onCloseModal();
				onSuccess && onSuccess()
			}
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	return (
		<Box>
			<Formik
				enableReinitialize
				initialValues={auctionFormInitialValues}
				onSubmit={handleSubmitClickHandler}
				validationSchema={validationSchema}
			>
				<FormContent onCloseModal={onCloseModal} edit={!!editableAuction} />
			</Formik>
		</Box>
	);
};

export default AuctionItem;
